/**
 * Typed error system for interview features
 */

export enum InterviewErrorCode {
  // Network errors
  NETWORK_TIMEOUT = 'NETWORK_TIMEOUT',
  NETWORK_ERROR = 'NETWORK_ERROR',

  // Voice call errors
  VOICE_CONNECTION_FAILED = 'VOICE_CONNECTION_FAILED',
  VOICE_NOT_INITIALIZED = 'VOICE_NOT_INITIALIZED',

  // Session errors
  SESSION_START_FAILED = 'SESSION_START_FAILED',
  SESSION_NOT_FOUND = 'SESSION_NOT_FOUND',
  SESSION_UPDATE_FAILED = 'SESSION_UPDATE_FAILED',

  // Call registration errors
  CALL_REGISTRATION_FAILED = 'CALL_REGISTRATION_FAILED',
  CALL_BINDING_FAILED = 'CALL_BINDING_FAILED',

  // Authentication errors
  USER_NOT_AUTHENTICATED = 'USER_NOT_AUTHENTICATED',
  UNAUTHORIZED = 'UNAUTHORIZED',

  // Interview limit errors
  INTERVIEW_LIMIT_REACHED = 'INTERVIEW_LIMIT_REACHED',

  // Validation errors
  INVALID_INPUT = 'INVALID_INPUT',

  // Generic errors
  INTERNAL_ERROR = 'INTERNAL_ERROR',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
}

export interface InterviewError {
  code: InterviewErrorCode;
  message: string;
  details?: unknown;
}

/**
 * Create a structured interview error
 */
export function createInterviewError(
  code: InterviewErrorCode,
  message: string,
  details?: unknown,
): InterviewError {
  return {
    code,
    message,
    details,
  };
}

/**
 * Check if an error is an InterviewError
 */
export function isInterviewError(error: unknown): error is InterviewError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error &&
    Object.values(InterviewErrorCode).includes((error as InterviewError).code)
  );
}

/**
 * Get user-friendly message for error code
 */
export function getErrorMessage(code: InterviewErrorCode): string {
  const messages: Record<InterviewErrorCode, string> = {
    [InterviewErrorCode.NETWORK_TIMEOUT]:
      'Connection timed out. Please check your internet connection and try again.',
    [InterviewErrorCode.NETWORK_ERROR]:
      'Network error occurred. Please check your connection.',
    [InterviewErrorCode.VOICE_CONNECTION_FAILED]:
      'Failed to connect to the interview system. Please try again.',
    [InterviewErrorCode.VOICE_NOT_INITIALIZED]:
      'Interview system not ready. Please refresh the page.',
    [InterviewErrorCode.SESSION_START_FAILED]:
      'Failed to start interview session. Please try again.',
    [InterviewErrorCode.SESSION_NOT_FOUND]:
      'Interview session not found. Please start a new interview.',
    [InterviewErrorCode.SESSION_UPDATE_FAILED]:
      'Failed to update interview session.',
    [InterviewErrorCode.CALL_REGISTRATION_FAILED]:
      'Failed to register call. The interview may not be saved properly.',
    [InterviewErrorCode.CALL_BINDING_FAILED]:
      'Failed to link call to session. The interview may not be saved properly.',
    [InterviewErrorCode.USER_NOT_AUTHENTICATED]:
      'Please log in to start an interview.',
    [InterviewErrorCode.UNAUTHORIZED]:
      'You are not authorized to perform this action.',
    [InterviewErrorCode.INTERVIEW_LIMIT_REACHED]:
      'You have reached your interview limit.',
    [InterviewErrorCode.INVALID_INPUT]:
      'Invalid input provided. Please check your request.',
    [InterviewErrorCode.INTERNAL_ERROR]:
      'Something went wrong. Please try again later.',
    [InterviewErrorCode.UNKNOWN_ERROR]:
      'An unexpected error occurred. Please try again.',
  };

  return messages[code] || messages[InterviewErrorCode.UNKNOWN_ERROR];
}
